import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  PublicClientApplication,
  IPublicClientApplication,
} from '@azure/msal-browser';
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { environment } from '../../../environments/environment';

/**
 * Factory for the MSAL PublicClientApplication instance.
 * Provided via MSAL_INSTANCE token in app.config.ts.
 */
export function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: window.location.origin,
      knownAuthorities: environment.msal.knownAuthorities,
      cloudDiscoveryMetadata: environment.msal.cloudDiscoveryMetadata,
      authorityMetadata: environment.msal.authorityMetadata,
    },
    cache: {
      cacheLocation: environment.msal.cacheLocation as BrowserCacheLocation,
    },
    system: {
      loggerOptions: {
        loggerCallback: (
          level: LogLevel,
          message: string,
          containsPii: boolean
        ) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
      },
    },
  });
}

/**
 * MsalGuard configuration — controls how the guard triggers login.
 */
export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.msal.scopes,
    },
  };
}

/**
 * MsalInterceptor configuration — maps protected URLs to their required scopes.
 */
export function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  // API requests (relative URLs through ingress/Front Door or localhost)
  const apiUrl = environment.apiUrl || window.location.origin;
  const apiScopes = environment.msal.scopes.filter(
    (s: string) => s.startsWith('api://') || s === 'access_as_user'
  );
  protectedResourceMap.set(`${apiUrl}/api/*`, apiScopes);

  // Metadata service API requests (direct connection or same-origin via ingress)
  const metadataApiUrl = environment.metadataApiUrl || window.location.origin;
  protectedResourceMap.set(`${metadataApiUrl}/api/*`, apiScopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
